import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Package, 
  Eye, 
  Edit, 
  Check, 
  X,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  MoreHorizontal,
  Download,
  RefreshCw,
  ShoppingCart,
  Tag,
  Star,
  Calendar,
  Image as ImageIcon,
  Zap,
  Target
} from 'lucide-react';
import ApiService from '../services/api';

interface ExtractedData {
  product_name: string;
  brand: string | null;
  price_old: number | null;
  price_new: number | null;
  discount: number;
  quantity: string | null;
  category: string | null;
  description?: string | null;
}

interface Product {
  id: number;
  offer_id: number;
  image_url: string | null;
  coordinates: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  };
  extracted_data: ExtractedData;
  confidence: number;
  status: string;
  needs_review: boolean;
  created_at: string;
  updated_at: string;
}

interface ProductStats {
  total: number;
  approved: number;
  rejected: number;
  pending: number;
  needs_review: number;
  avg_confidence: number;
}

const ProductsManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ProductStats | null>(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  // تحميل البيانات
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 تحميل المنتجات...');
      
      // تحميل المنتجات
      const productsResponse = await ApiService.getProducts({ limit: 200 });

      console.log('📦 Products loaded:', productsResponse);
      
      setProducts(productsResponse || []);
      
      // حساب الإحصائيات محلياً
      if (productsResponse?.length) {
        const approved = productsResponse.filter((p: Product) => p.status === 'approved').length;
        const rejected = productsResponse.filter((p: Product) => p.status === 'rejected').length;
        const pending = productsResponse.filter((p: Product) => p.status === 'pending').length;
        const needsReview = productsResponse.filter((p: Product) => p.needs_review).length;
        const avgConfidence = productsResponse.reduce((sum: number, p: Product) => sum + p.confidence, 0) / productsResponse.length;
        
        setStats({
          total: productsResponse.length,
          approved,
          rejected,
          pending,
          needs_review: needsReview,
          avg_confidence: avgConfidence
        });
      } else {
        setStats({
          total: 0,
          approved: 0,
          rejected: 0,
          pending: 0,
          needs_review: 0,
          avg_confidence: 0
        });
      }
      
    } catch (err) {
      console.error('❌ Error loading products:', err);
      setError('فشل في تحميل البيانات. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  // إعادة تحميل البيانات
  const handleRefresh = () => {
    loadData();
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('ar-SA');
    } catch {
      return dateString;
    }
  };

  // فلترة المنتجات
  const filteredProducts = products.filter(product => {
    const productName = product.extracted_data?.product_name || '';
    const brand = product.extracted_data?.brand || '';
    const category = product.extracted_data?.category || '';
    
    const matchesSearch = productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || product.status === statusFilter;
    const matchesCategory = categoryFilter === 'all' || category === categoryFilter;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  // الحصول على الفئات الفريدة
  const uniqueCategories = [...new Set(
    products
      .map(p => p.extracted_data?.category)
      .filter(Boolean)
  )];

  // التصفح بالصفحات
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const currentProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSelectProduct = (productId: number) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    setSelectedProducts(
      selectedProducts.length === currentProducts.length 
        ? [] 
        : currentProducts.map(p => p.id)
    );
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-12 h-12 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">جاري تحميل المنتجات...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-error-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">خطأ في تحميل البيانات</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={handleRefresh} className="btn-primary">
            <RefreshCw className="w-4 h-4 ml-2" />
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <div>
          <h1 className="text-responsive-xl font-bold gradient-text">
            إدارة المنتجات المستخرجة
          </h1>
          <p className="text-gray-600 mt-2">
            مراجعة وإدارة المنتجات التي تم استخراجها من الفلايرز
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleRefresh} className="btn-outline">
            <RefreshCw className="w-4 h-4 ml-2" />
            تحديث
          </button>
          <button className="btn-secondary">
            <Download className="w-4 h-4 ml-2" />
            تصدير
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="metrics-grid">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="stat-card-modern group"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="icon-primary">
                  <Package className="w-6 h-6" />
                </div>
                <div className="text-sm font-medium text-primary-600">
                  المجموع
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                  {stats.total}
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-1">إجمالي المنتجات</p>
                <p className="text-xs text-gray-600">المستخرجة من الفلايرز</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="stat-card-modern group"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="icon-success">
                  <Check className="w-6 h-6" />
                </div>
                <div className="text-sm font-medium text-success-600">
                  +{Math.round((stats.approved / stats.total) * 100)}%
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                  {stats.approved}
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-1">منتجات مقبولة</p>
                <p className="text-xs text-gray-600">تمت مراجعتها وقبولها</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="stat-card-modern group"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="icon-warning">
                  <AlertCircle className="w-6 h-6" />
                </div>
                <div className="text-sm font-medium text-warning-600">
                  {stats.pending} معلق
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                  {stats.needs_review}
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-1">بانتظار المراجعة</p>
                <p className="text-xs text-gray-600">تحتاج لمراجعة يدوية</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="stat-card-modern group"
          >
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="icon-indigo">
                  <Target className="w-6 h-6" />
                </div>
                <div className="text-sm font-medium text-indigo-600">
                  الدقة
                </div>
              </div>
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-1 group-hover:scale-105 transition-transform duration-300">
                  {Math.round(stats.avg_confidence * 100)}%
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-1">متوسط الثقة</p>
                <p className="text-xs text-gray-600">دقة الذكاء الاصطناعي</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-gradient"
      >
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="البحث في المنتجات..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input pr-10"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input"
            >
              <option value="all">جميع الحالات</option>
              <option value="approved">مقبول</option>
              <option value="pending">معلق</option>
              <option value="rejected">مرفوض</option>
            </select>
            
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="input"
            >
              <option value="all">جميع الفئات</option>
              {uniqueCategories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={handleSelectAll}
                className="btn-outline flex-1"
              >
                {selectedProducts.length === currentProducts.length ? 'إلغاء الكل' : 'تحديد الكل'}
              </button>
              {selectedProducts.length > 0 && (
                <button className="btn-primary">
                  إجراءات ({selectedProducts.length})
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Products Grid */}
      {currentProducts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {currentProducts.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`card-gradient group hover-lift cursor-pointer ${
                selectedProducts.includes(product.id) ? 'ring-2 ring-primary-500' : ''
              }`}
              onClick={() => handleSelectProduct(product.id)}
            >
              <div className="relative">
                {product.image_url ? (
                  <div className="h-48 bg-gray-100 rounded-t-2xl overflow-hidden">
                    <img
                      src={product.image_url}
                      alt={product.extracted_data?.product_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.currentTarget.parentElement!.innerHTML = `
                          <div class="h-48 bg-gray-100 rounded-t-2xl flex items-center justify-center">
                            <div class="text-gray-400 text-center">
                              <svg class="w-12 h-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
                              </svg>
                              <p class="text-sm">لا توجد صورة</p>
                            </div>
                          </div>
                        `;
                      }}
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-gray-100 rounded-t-2xl flex items-center justify-center">
                    <div className="text-gray-400 text-center">
                      <ImageIcon className="w-12 h-12 mx-auto mb-2" />
                      <p className="text-sm">لا توجد صورة</p>
                    </div>
                  </div>
                )}
                
                <div className="absolute top-3 right-3 flex gap-2">
                  <span className={`badge badge-${getConfidenceColor(product.confidence)}`}>
                    {Math.round(product.confidence * 100)}%
                  </span>
                  {selectedProducts.includes(product.id) && (
                    <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              </div>
              
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                  {product.extracted_data?.product_name || 'منتج غير محدد'}
                </h3>
                
                <div className="space-y-2 mb-3">
                  {product.extracted_data?.brand && (
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{product.extracted_data.brand}</span>
                    </div>
                  )}
                  
                  {product.extracted_data?.category && (
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{product.extracted_data.category}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {product.extracted_data?.price_new && (
                      <span className="text-lg font-bold text-success-600">
                        {product.extracted_data.price_new} ريال
                      </span>
                    )}
                    {product.extracted_data?.price_old && 
                     product.extracted_data?.price_old > product.extracted_data?.price_new && (
                      <span className="text-sm text-gray-500 line-through">
                        {product.extracted_data.price_old}
                      </span>
                    )}
                  </div>
                  
                  {product.extracted_data?.discount > 0 && (
                    <span className="badge badge-error">
                      -{Math.round(product.extracted_data.discount)}%
                    </span>
                  )}
                </div>
                
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(product.created_at)}
                  </span>
                  <button className="text-primary-600 hover:text-primary-700">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-16"
        >
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">لا توجد منتجات</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || statusFilter !== 'all' || categoryFilter !== 'all'
              ? 'لا توجد منتجات تطابق معايير البحث'
              : 'لم يتم استخراج أي منتجات بعد'
            }
          </p>
          {(!searchTerm && statusFilter === 'all' && categoryFilter === 'all') && (
            <button className="btn-primary">
              ابدأ معالجة الفلايرز
            </button>
          )}
        </motion.div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex justify-center gap-2"
        >
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="btn-outline"
          >
            السابق
          </button>
          
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                  currentPage === page
                    ? 'bg-primary-500 text-white'
                    : 'bg-white text-gray-700 hover:bg-primary-50'
                }`}
              >
                {page}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="btn-outline"
          >
            التالي
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default ProductsManagement;